// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import resetEditingAddress from "@bg/App/Handlers/OnePageCheckout/ResetEditingAddress";
import xSetUseBillingAddress from "@bg/App/Handlers/OnePageCheckout/SetUseBillingAddress";
import xSetUseOneTimeAddress from "@bg/App/Handlers/OnePageCheckout/SetUseOneTimeAddress";
import { CustomAppState } from "@bg/App/Store/Reducers";
import CheckoutShippingAddressForm from "@bg/App/Widgets/OnePageCheckout/CheckoutShippingAddressForm";
import ShippingAddressInfo from "@bg/App/Widgets/OnePageCheckout/ShippingAddressInfo";
import ShipToSelectorModal from "@bg/Overrides/Widgets/OnePageCheckout/ShipToSelectorModal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import OnePageCheckoutState from "@insite/client-framework/Store/Pages/OnePageCheckout/OnePageCheckoutState";
import translate from "@insite/client-framework/Translate";
import { ShipToSelectorModalStyles } from "@insite/content-library/Widgets/OnePageCheckout/ShipToSelectorModal";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Radio from "@insite/mobius/Radio/Radio";
import RadioGroup, { RadioGroupComponentProps, RadioGroupProps } from "@insite/mobius/RadioGroup/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    cart?: Cart;
}

const mapStateToProps = (state: CustomAppState, { cart }: OwnProps) => {
    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;

    let newAddress;
    let showOneTimeAddress;
    let isShipToSameAsBillTo = false;
    let showUseBillingAddress = false;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
        showOneTimeAddress = shipTos!.find(s => s.oneTimeAddress);
        isShipToSameAsBillTo = cart.billToId === cart.shipToId;
        showUseBillingAddress = shipTos!.some(shipTo => shipTo.id === cart.billToId);
    }

    return {
        countries: getCurrentCountries(state),
        shipToState: getShipToState(state, cart?.shipToId),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        newAddress,
        showOneTimeAddress,
        isShipToSameAsBillTo,
        showUseBillingAddress,
        shippingAddressFormState: state.pages.onePageCheckout.shippingAddressFormState,
        useOneTimeAddress: state.xOnePageCheckout.useOneTimeAddress,
        useBillingAddress: state.xOnePageCheckout.useBillingAddress,
    };
};

const mapDispatchToProps = {
    xSetUseOneTimeAddress,
    xSetUseBillingAddress,
    setAddressBookOverlayIsOpen,
    resetEditingAddress,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

interface OnePageCheckoutShippingAddressStyles {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    heading?: TypographyPresentationProps;
    selectorRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    addressSection?: InjectableCss;
    divider?: InjectableCss;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    editSavedAddressLink?: LinkPresentationProps;
}

export const onePageCheckoutShippingAddressStyles: OnePageCheckoutShippingAddressStyles = {};

const styles = onePageCheckoutShippingAddressStyles;
const OnePageCheckoutShippingAddress: FC<Props> = ({
    cart,
    countries,
    shipToState,
    showOneTimeAddress,
    isShipToSameAsBillTo,
    showUseBillingAddress,
    shippingAddressFormState,
    useOneTimeAddress,
    xSetUseOneTimeAddress,
    useBillingAddress,
    xSetUseBillingAddress,
    setAddressBookOverlayIsOpen,
    newAddress,
    currentUserIsGuest,
    allowCreateNewShipToAddress,
    resetEditingAddress,
    theme,
}) => {
    // XNG-Changes: Use to blur radio button
    const containerRef = useRef<HTMLDivElement>(null);

    /**
     * XNG-Changes
     * To Add "Edit Address" and handle it
     */
    const [shippingAddressFormStateSource, setShippingAddressFormStateSource] = useState(shippingAddressFormState);
    const [isShipToSelectorModalOpen, setIsShipToSelectorModalOpen] = useState(false);
    const shipTo = shipToState.value;

    const editShippingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsShipToSelectorModalOpen(true);
    };

    const shipToSelectorModalCloseHandler = () => {
        setIsShipToSelectorModalOpen(false);
        setTimeout(() => {
            (
                containerRef.current?.querySelector(
                    "[data-test-selector='checkoutShipping_selectSavedAddresses']",
                ) as HTMLInputElement
            ).blur();
        }, 500);
    };

    /**
     * XNG-Changes
     * Update useOneTimeAddress & useBillingAddress
     */
    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        resetEditingAddress();
        if (event.target.value === "onetime") {
            xSetUseOneTimeAddress({
                useOneTimeAddress: true,
            });
        } else if (event.target.value === "billing") {
            xSetUseBillingAddress({
                useBillingAddress: true,
            });
        } else if (event.target.value === "savedAddresses") {
            editShippingAddressClickHandler();
        }
    };

    const updateShippingAddressFormStateSource = (
        newShippingAddressFormState: OnePageCheckoutState["shippingAddressFormState"],
    ) => {
        setShippingAddressFormStateSource(newShippingAddressFormState);
    };

    if (!countries || !shipTo) {
        return null;
    }

    const getSelectedAddressType = (): "billing" | "savedAddresses" | "onetime" | undefined => {
        if (showOneTimeAddress && showUseBillingAddress) {
            return shippingAddressFormState
                ? shippingAddressFormState.address.oneTimeAddress
                    ? "onetime"
                    : shippingAddressFormState.address.id === cart?.billToId
                      ? "billing"
                      : "savedAddresses"
                : undefined;
        }
        if (showOneTimeAddress && (useOneTimeAddress || shippingAddressFormState?.address.oneTimeAddress)) {
            return "onetime";
        }
        if (
            showUseBillingAddress &&
            (useBillingAddress || isShipToSameAsBillTo || shippingAddressFormState?.address.id === cart?.billToId)
        ) {
            return "billing";
        }

        return "savedAddresses";
    };

    const selectedAddressType = getSelectedAddressType();
    const showSelectedSavedAddresses =
        !currentUserIsGuest || (currentUserIsGuest && (allowCreateNewShipToAddress || !!newAddress || !!shipTo));

    /**
     * XNG-Changes
     * Update Structure
     * Add condition for Quote
     * Add RadioGroup to handle OneTimeAddress and SavedAddresses
     */
    return (
        <StyledWrapper {...styles.container} ref={containerRef}>
            <StyledWrapper {...styles.titleContainer}>
                <Typography {...styles.heading}>{translate("Shipping Address")}</Typography>
            </StyledWrapper>
            <RadioGroup
                value={selectedAddressType}
                onChangeHandler={addressTypeChangeHandler}
                {...styles.selectorRadioGroup}
            >
                {showUseBillingAddress && (
                    <>
                        <Radio value="billing" data-test-selector="checkoutShipping_billingAddress">
                            {translate("Use Billing Address")}
                        </Radio>
                        {selectedAddressType === "billing" && (
                            <StyledWrapper {...styles.addressSection} data-test-selector="BillingAddressSection">
                                <CheckoutShippingAddressForm
                                    cart={cart}
                                    shippingAddressFormStateSource={shippingAddressFormStateSource}
                                    countries={countries}
                                />
                                <ShippingAddressInfo
                                    cart={cart}
                                    updateShippingAddressFormStateSource={updateShippingAddressFormStateSource}
                                />
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.divider} />
                    </>
                )}
                {showSelectedSavedAddresses && (
                    <>
                        <Radio value="savedAddresses" data-test-selector="checkoutShipping_selectSavedAddresses">
                            {translate("Select Saved Addresses")}
                        </Radio>
                        {selectedAddressType === "savedAddresses" && (
                            <StyledWrapper {...styles.addressSection} data-test-selector="OneTimeAddressSection">
                                <CheckoutShippingAddressForm
                                    cart={cart}
                                    shippingAddressFormStateSource={shippingAddressFormStateSource}
                                    countries={countries}
                                />
                                <ShippingAddressInfo
                                    cart={cart}
                                    updateShippingAddressFormStateSource={updateShippingAddressFormStateSource}
                                />
                                <Link
                                    {...styles.editSavedAddressLink}
                                    onClick={editShippingAddressClickHandler}
                                    data-test-selector="editShippingAddressButton"
                                >
                                    {translate("Edit / Select New Address")}
                                    <VisuallyHidden>{translate("Shipping Address")}</VisuallyHidden>
                                </Link>
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.divider} />
                    </>
                )}
                {showOneTimeAddress && (
                    <>
                        <Radio value="onetime" data-test-selector="checkoutShipping_useOneTimeAddress">
                            {translate("Ship to One-Time Address")}
                        </Radio>
                        {selectedAddressType === "onetime" && (
                            <StyledWrapper {...styles.addressSection} data-test-selector="OneTimeAddressSection">
                                <CheckoutShippingAddressForm
                                    cart={cart}
                                    shippingAddressFormStateSource={shippingAddressFormStateSource}
                                    countries={countries}
                                />
                                <ShippingAddressInfo
                                    cart={cart}
                                    updateShippingAddressFormStateSource={updateShippingAddressFormStateSource}
                                />
                            </StyledWrapper>
                        )}
                    </>
                )}
            </RadioGroup>
            {showSelectedSavedAddresses && (
                <ShipToSelectorModal
                    isOpen={isShipToSelectorModalOpen}
                    onModalClose={shipToSelectorModalCloseHandler}
                    extendedStyles={styles.shipToSelectorModal}
                />
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(OnePageCheckoutShippingAddress));
